import { useState } from 'react';
import { useQuery } from '../hooks/useQuery';
import { useTableSort } from '../hooks/useTableSort';
import { api, type WebhookReceiptRow } from '../lib/api';
import {
  Badge, Card, CardHeader, EmptyState, MetricCard, SectionLabel,
  SortableTh, Spinner, Table, Td, Th, Tr,
} from '../components/ui';
import { InvoiceLink } from '../components/InvoiceLink';

/**
 * ChargeOver webhook receipt log. One row per inbound webhook (incl.
 * pings + ignored events + errors). Pulls from the audit_log table,
 * filtered to `action = 'chargeover_webhook_received'`. Columns map
 * onto the JSON-detail fields the handler stamps.
 *
 * The data lives in audit_log not a dedicated table because it's
 * exactly the shape audit_log was designed for: timestamped event,
 * structured detail. A dedicated table would duplicate.
 */

type Outcome =
  | 'ping'
  | 'invoice-auto-approved'
  | 'deposit-credited'
  | 'deposit-credit-reused'
  | 'no-deposit-line-items'
  | 'no-matching-rule'
  | 'no-invoice'
  | 'context-not-handled'
  | 'error'
  | 'ignored';

interface NormalisedRow {
  id: string;
  receivedAt: string;
  context: string;
  event: string;
  invoiceId: string | null;
  customerId: string | null;
  outcome: Outcome;
  invoiceStatusBefore: string | null;
  custom2: string | null;
  depositAmount: number | null;
  error: string | null;
  /** Raw detail JSON for the per-row expand panel. */
  raw: Record<string, unknown> | null;
}

function normalise(r: WebhookReceiptRow): NormalisedRow {
  const d = r.detail ?? {};
  const outcomeRaw = (d as { outcome?: string }).outcome ?? 'ignored';
  const outcome: Outcome =
    (['ping', 'invoice-auto-approved', 'deposit-credited', 'deposit-credit-reused',
      'no-deposit-line-items', 'no-matching-rule', 'no-invoice', 'context-not-handled',
      'error', 'ignored'] as const).includes(outcomeRaw as Outcome)
      ? outcomeRaw as Outcome
      : 'ignored';
  return {
    id: r.id,
    receivedAt: r.created_at,
    context: String((d as { context_str?: string }).context_str ?? ''),
    event: String((d as { event?: string }).event ?? ''),
    invoiceId: r.entity_id,
    customerId: stringOrNull((d as { customer_id?: unknown }).customer_id),
    outcome,
    invoiceStatusBefore: stringOrNull((d as { invoice_status_name?: unknown }).invoice_status_name),
    custom2: stringOrNull((d as { custom_2?: unknown }).custom_2),
    depositAmount: numberOrNull((d as { deposit_amount?: unknown }).deposit_amount),
    error: stringOrNull((d as { error?: unknown }).error),
    raw: r.detail,
  };
}

function stringOrNull(v: unknown): string | null {
  if (v == null || v === '') return null;
  return String(v);
}
function numberOrNull(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

function fmtDt(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return iso;
  }
}

const OUTCOME_VARIANT: Record<Outcome, 'green' | 'amber' | 'blue' | 'red' | 'gray'> = {
  'ping': 'gray',
  'invoice-auto-approved': 'green',
  'deposit-credited': 'green',
  'deposit-credit-reused': 'blue',
  'no-deposit-line-items': 'gray',
  'no-matching-rule': 'gray',
  'no-invoice': 'gray',
  'context-not-handled': 'gray',
  'error': 'red',
  'ignored': 'gray',
};

export default function AdminWebhooksPage() {
  const { data, isLoading, error, refetch } = useQuery(() => api.admin.webhooks.chargeover(200));
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (isLoading) return <Spinner />;
  if (error) return <EmptyState message={`Error: ${error}`} />;

  const rows = (data?.webhooks ?? []).map(normalise);
  const last24h = rows.filter((r) => Date.now() - new Date(r.receivedAt).getTime() < 24 * 3600 * 1000);
  const errors24h = last24h.filter((r) => r.outcome === 'error').length;
  const actions24h = last24h.filter((r) => r.outcome === 'invoice-auto-approved' || r.outcome === 'deposit-credited' || r.outcome === 'deposit-credit-reused').length;
  const ignored24h = last24h.length - errors24h - actions24h;

  return (
    <>
      <SectionLabel>ChargeOver webhooks</SectionLabel>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10 }}>
        <MetricCard label="Last 24h" value={last24h.length} sub={`${rows.length} total in log`} />
        <MetricCard label="Actions taken" value={actions24h} accent={actions24h > 0 ? 'green' : undefined} sub="auto-approved + deposits" />
        <MetricCard label="Ignored" value={ignored24h} sub="pings + no-matching-rule" />
        <MetricCard label="Errored" value={errors24h} accent={errors24h > 0 ? 'amber' : undefined} sub="ChargeOver will have retried" />
      </div>

      <Card>
        <CardHeader>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Receipt log</span>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
            Newest first; click a row to see the raw detail JSON. Capped at 200 most recent.
          </span>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
            <button
              type="button"
              onClick={() => refetch()}
              style={{
                fontSize: 11, padding: '4px 10px', borderRadius: 'var(--radius-sm)',
                border: '0.5px solid var(--color-border-strong)',
                background: 'var(--color-surface)', color: 'var(--color-text)',
                cursor: 'pointer',
              }}
            >
              Refresh
            </button>
          </span>
        </CardHeader>
        <WebhooksTable rows={rows} expandedId={expandedId} setExpandedId={setExpandedId} />
      </Card>
    </>
  );
}

function WebhooksTable({
  rows, expandedId, setExpandedId,
}: {
  rows: NormalisedRow[];
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
}) {
  const { sorted, sort } = useTableSort(rows, {
    received: (r: NormalisedRow) => r.receivedAt,
    context:  (r: NormalisedRow) => r.context,
    event:    (r: NormalisedRow) => r.event,
    invoice:  (r: NormalisedRow) => r.invoiceId,
    outcome:  (r: NormalisedRow) => r.outcome,
  }, { key: 'received', dir: 'desc' });

  if (rows.length === 0) return <EmptyState message="No webhooks received yet." />;
  return (
    <Table>
      <thead>
        <tr>
          <SortableTh columnKey="received" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Received</SortableTh>
          <SortableTh columnKey="context"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Context / event</SortableTh>
          <SortableTh columnKey="invoice"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Invoice</SortableTh>
          <Th>Status before</Th>
          <SortableTh columnKey="outcome"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Outcome</SortableTh>
          <Th>Notes</Th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((r) => {
          const isOpen = expandedId === r.id;
          return (
            <>
              <Tr
                key={r.id}
                onClick={() => setExpandedId(isOpen ? null : r.id)}
                selected={isOpen}
                style={r.outcome === 'error' ? { background: 'var(--color-red-bg)' } : undefined}
              >
                <Td muted style={{ fontSize: 11 }}>{fmtDt(r.receivedAt)}</Td>
                <Td muted style={{ fontSize: 11 }}>
                  <span style={{ fontWeight: 500, color: 'var(--color-text)' }}>{r.context || '—'}</span>
                  {r.event && (
                    <span style={{ marginLeft: 4, color: 'var(--color-text-muted)' }}>· {r.event}</span>
                  )}
                </Td>
                <Td muted style={{ fontSize: 11 }}>
                  <InvoiceLink invoiceId={r.invoiceId} />
                  {r.customerId && (
                    <div style={{ fontSize: 10, color: 'var(--color-text-hint)' }}>cust {r.customerId}</div>
                  )}
                </Td>
                <Td muted style={{ fontSize: 11 }}>
                  {r.invoiceStatusBefore ?? '—'}
                  {r.custom2 && (
                    <div style={{ fontSize: 10, color: 'var(--color-text-hint)' }}>
                      custom_2={r.custom2}
                    </div>
                  )}
                </Td>
                <Td><Badge variant={OUTCOME_VARIANT[r.outcome]}>{r.outcome}</Badge></Td>
                <Td muted style={{
                  fontSize: 11,
                  color: r.error ? 'var(--color-red-text)' : 'var(--color-text-muted)',
                  maxWidth: 360,
                }}>
                  {r.error
                    ?? (r.depositAmount != null ? `$${r.depositAmount.toFixed(2)} deposit` : '—')}
                </Td>
              </Tr>
              {isOpen && (
                <tr key={`${r.id}-detail`}>
                  <td colSpan={6} style={{ padding: '8px 16px 14px', background: 'var(--color-bg)' }}>
                    <pre style={{
                      margin: 0, fontSize: 10, lineHeight: 1.4,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      color: 'var(--color-text-muted)',
                    }}>
                      {JSON.stringify(r.raw, null, 2)}
                    </pre>
                  </td>
                </tr>
              )}
            </>
          );
        })}
      </tbody>
    </Table>
  );
}
